import React, { useState, useRef, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import "./../../../assets/css/purchase/datatable.css";
import Pagination from "../Pagination";

export default function ServiceLocationTable({
  locations = [],
  serviceTypes = [],
  servicePurposes = [],
  onEdit,
  onDelete,
  onAdd,
  onAddAppointment,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [appointmentRight, setAppointmentRight] = useState(130);
  const tableRef = useRef(null);
  const actionColRef = useRef(null);
  const serviceTypeMap = useMemo(() => {
    const map = new Map();
    serviceTypes.forEach((item) => {
      map.set(String(item.id), item.name);
    });
    return map;
  }, [serviceTypes]);

  const servicePurposeMap = useMemo(() => {
    const map = new Map();
    servicePurposes.forEach((item) => {
      map.set(String(item.id), item.name);
    });
    return map;
  }, [servicePurposes]);

  const handleDelete = (loc) => {
    onDelete?.(loc);
  };

  const totalItems = locations.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = locations.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    const updateAppointmentRight = () => {
      if (actionColRef.current && tableRef.current) {
        const actionWidth = actionColRef.current.offsetWidth;
        setAppointmentRight(actionWidth);
      }
    };

    // ใช้ setTimeout เพื่อให้ DOM render เสร็จก่อน
    const timer = setTimeout(updateAppointmentRight, 0);
    window.addEventListener("resize", updateAppointmentRight);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateAppointmentRight);
    };
  }, [locations, currentPage, itemsPerPage]);

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm pl-3"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(91vh - 320px)",
        overflow: "hidden",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }

        table.table {
          border-collapse: separate !important;
          border-spacing: 0 !important;
          width: 100%;
          min-width: 1300px;
          font-size: 14px !important;
        }

        th, td {
          text-align: center !important;
          vertical-align: middle !important;
          border: 1px solid #ccc !important;
          padding: 6px 10px !important;
          white-space: nowrap;
          font-size: 14px !important;
        }

        thead th {
          background-color: #E13F57 !important;
          color: white !important;
          font-weight: 600;
        }

        tbody tr:nth-child(odd) { background: #fafafa; }
        tbody tr:nth-child(even) { background: #fff; }

        td.sticky-appointment, th.sticky-appointment {
          position: sticky;
          right: ${appointmentRight}px;
          background: white !important;
          z-index: 20;
          isolation: isolate;
          box-shadow: -3px 0 5px rgba(0,0,0,0.12);
        }

        tbody tr:nth-child(odd) td.sticky-appointment {
          background: #fafafa !important;
        }

        tbody tr:nth-child(even) td.sticky-appointment {
          background: #fff !important;
        }

        tbody tr:hover td.sticky-appointment {
          background: #f5f7ff !important;
        }

        thead th.sticky-appointment {
          background-color: #E13F57 !important;
          z-index: 21 !important;
        }

        td.sticky-action, th.sticky-action {
          position: sticky;
          right: 0;
          background: white !important;
          z-index: 20;
          isolation: isolate;
          box-shadow: -3px 0 5px rgba(0,0,0,0.12);
        }

        thead th.sticky-action {
          background-color: #E13F57 !important;
          z-index: 21 !important;
        }

        tbody tr:hover { background: #f5f7ff !important; }

        button, a, small, span { font-size: 14px !important; }
      `}</style>

      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
        <h5 className="m-0 fw-bold">ข้อมูลสถานที่บริการ</h5>

        <button
          onClick={onAdd}
          className="flex items-center gap-1 text-[#F17F25] text-sm font-semibold border-b-2 border-[#F17F25] pb-0.5 hover:text-[#c7641c] transition"
        >
          <Icon icon="mdi:plus-circle-outline" className="w-5 h-5" /> เพิ่มข้อมูลสถานที่บริการ
        </button>
      </div>

      <div
        className="table-wrapper-scroll flex-1"
        style={{
          overflowY: "auto",
          overflowX: "auto",
          flexGrow: 1,
          padding: "0",
        }}
      >
        <table ref={tableRef} className="table table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th style={{ width: "60px" }}>ลำดับ</th>
              <th style={{ width: "15%" }}>ประเภท</th>
              <th style={{ width: "15%" }}>วัตถุประสงค์</th>
              <th style={{ width: "10%" }}>ชื่อสถานที่บริการ</th>
              <th style={{ width: "35%" }}>ข้อมูลสถานที่บริการ</th>
              <th style={{ width: "15%" }}>หมายเหตุ</th>
              <th style={{ width: "25%" }}>ข้อมูลสเปค</th>
              <th className="sticky-appointment" style={{ width: "10%" }}>นัดหมาย</th>
              <th ref={actionColRef} className="sticky-action" style={{ width: "10%" }}>การจัดการ</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((loc, i) => {
                const typeName =
                  serviceTypeMap.get(String(loc.ServiceAddr_Type_Id)) || "-";

                const purposeName =
                  servicePurposeMap.get(String(loc.ServicePurpose)) || "-";

                const fullAddr = [
                  loc.AddrNo,
                  loc.Building,
                  loc.Village,
                  loc.Moo,
                  loc.Soi,
                  loc.Road,
                  loc.SubDistrictName,
                  loc.DistrictName,
                  loc.ProvinceName,
                  loc.Zipcode,
                ]
                  .filter(Boolean)
                  .join(" ");

                const specText = [
                  loc.Width ? `กว้าง ${loc.Width} เมตร` : null,
                  loc.Length ? `ยาว ${loc.Length} เมตร` : null,
                  loc.Height ? `สูง ${loc.Height} ชั้น` : null,
                  loc.SpecRemark ? `(${loc.SpecRemark})` : null,
                ]
                  .filter(Boolean)
                  .join(" , ");

                return (
                  <tr key={loc.ServiceAddr_Id || i}>
                    <td>{(currentPage - 1) * itemsPerPage + (i + 1)}</td>
                    <td>{typeName}</td>
                    <td>{purposeName}</td>
                    <td className="text-start">{loc.ServiceAddr_Name || "-"}</td>
                    <td className="text-start">{fullAddr || "-"}</td>
                    <td>{loc.AddrRemark || "-"}</td>
                    <td className="text-start">{specText || "-"}</td>

                    <td className="sticky-appointment">
                      <button
                        onClick={() => onAddAppointment?.(loc)}
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#7759E4" }}
                      >
                        นัดหมาย
                      </button>
                    </td>

                    <td className="sticky-action">
                      <div className="d-flex justify-content-center gap-2 flex-nowrap">
                        <button
                          onClick={() => onEdit?.(loc)}
                          className="btn btn-sm text-white d-flex align-items-center gap-1"
                          style={{ backgroundColor: "#598AE4" }}
                        >
                          แก้ไข
                        </button>

                        <button
                          onClick={() => handleDelete(loc)}
                          className="btn btn-sm text-white d-flex align-items-center gap-1"
                          style={{ backgroundColor: "#E13F57" }}
                        >
                          ลบ
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-4 text-muted">
                  ไม่มีข้อมูลสถานที่บริการ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-2 py-2">
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(val) => {
            setItemsPerPage(val);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
}
